/**
 * useCameraHandoff — la caméra de l'entrée rendue avant d'entrer dans le live.
 *
 * L'entrée façon Meet garde l'image de la caméra jusqu'à « Rejoindre » (`CameraView` d'expo-camera).
 * Le live ouvre ensuite la sienne (LiveKit). Sur un téléphone, un objectif ne sert qu'un client à la
 * fois : si l'entrée le tient encore quand le live l'ouvre, la vignette « Toi » reste noire alors que
 * la piste est publiée (vu une fois sur l'émulateur Android le 18/10 ; non reproduit le 19/10, où
 * l'objectif était rendu en 0,25 s et rouvert 0,9 s après « Rejoindre »). L'ordre ne tient donc
 * qu'au temps : on le rend sûr. « Rejoindre » démonte d'abord l'image, puis entre une fois l'objectif
 * rendu — `CameraView` ne dit pas quand, d'où l'attente bornée.
 *
 * Le web ne change pas : un navigateur ouvre la même caméra pour deux clients, et l'entrée y rend ses
 * pistes elle-même. Le précall du masteur y ajoute son micro (l'enregistreur de l'étape du micro).
 */
import { useCallback, useEffect, useRef, useState } from "react"

import { IS_WEB } from "../../../../../../engine/shared/platform"

/** Le temps laissé à l'objectif pour être rendu : 0,25 s mesuré sur l'émulateur, avec de la marge. */
export const CAMERA_HANDOFF_MS = 600

/**
 * La remise : `enter(holding)` appelle `onEntered` tout de suite quand l'écran ne tient aucun appareil
 * (ou au web), sinon passe `releasing` à vrai — l'écran démonte alors l'image et lâche le micro — et
 * appelle `onEntered` au bout de `CAMERA_HANDOFF_MS`. Ce qui est tenu se dit au geste, pas à l'appel du
 * crochet : le masteur le lit sur le test du matériel, dont le moteur a besoin de `releasing` pour
 * fermer son micro (`tutor/PreCall/useTutorPreCallState`).
 */
export function useCameraHandoff(onEntered: () => void) {
    const [releasing, setReleasing] = useState(false)
    const onEnteredRef = useRef(onEntered)
    onEnteredRef.current = onEntered

    const enter = useCallback((holding: boolean) => {
        if (IS_WEB || !holding) {
            onEnteredRef.current()
            return
        }
        setReleasing(true)
    }, [])

    // Les appareils lâchés (ce rendu-ci), ils ont `CAMERA_HANDOFF_MS` pour être rendus.
    useEffect(() => {
        if (!releasing) return undefined
        const timer = setTimeout(() => onEnteredRef.current(), CAMERA_HANDOFF_MS)
        return () => clearTimeout(timer)
    }, [releasing])

    return { releasing, enter }
}
